/**
 * Top-center banner for system notifications (e.g. version updates).
 * Shows when there is a new, un-dismissed notification from the WebSocket.
 * User clicks "Okay, Refresh" → code saved to localStorage, page reloads.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '@contexts/ThemeContext';
import { SystemNotification } from '@hooks/useNotificationWebSocket';

interface SystemNotificationBannerProps {
  notification: SystemNotification | null;
  onDismiss: () => void;
}

const SystemNotificationBanner: React.FC<SystemNotificationBannerProps> = ({
  notification,
  onDismiss,
}) => {
  const { colors: themeColors } = useTheme();

  const handleClick = () => {
    onDismiss();
    // Force a hard reload bypassing cache (like Cmd+Shift+R) so
    // the user picks up the latest deployed assets.
    // Preserve existing query params (e.g. batchUUID) and only set/replace _t.
    const url = new URL(window.location.href);
    url.searchParams.set('_t', String(Date.now()));
    window.location.href = url.toString();
  };

  return (
    <AnimatePresence>
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed top-4 z-[10000] pointer-events-none flex justify-center"
          style={{ left: 88, right: 0 }}
        >
          <div
            className="flex items-center gap-3 rounded-lg px-5 py-3 shadow-lg border pointer-events-auto"
            style={{
              backgroundColor: themeColors.card,
              borderColor: themeColors.primary,
              boxShadow: `0 4px 24px ${themeColors.primary}30`,
            }}
          >
            {/* Icon */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${themeColors.primary}20` }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke={themeColors.primary}
                role="presentation"
              >
                <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            {/* Message */}
            <p
              className="text-sm font-medium max-w-md"
              style={{ color: themeColors.text }}
            >
              {notification.message}
            </p>

            {/* Action button */}
            <button
              type="button"
              onClick={handleClick}
              className="ml-2 px-4 py-1.5 rounded-md text-xs font-semibold text-white transition-colors flex-shrink-0 cursor-pointer"
              style={{
                backgroundColor: themeColors.primary,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  themeColors.primaryHover;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  themeColors.primary;
              }}
            >
              Okay, Refresh
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SystemNotificationBanner;
